// The Stream connection lifecycle of the two chat clients (CHT-1996): the staff web (`apps/admin`, `stream-chat`) and the customer app
// (`apps/mobile`, `stream-chat-expo` over the same `stream-chat` core). Each client keeps its own message mapping, storage and screens in its
// own adapter; the part that is the same for both lives here, once:
//
//   1. connect the user BEFORE a channel is made: the SDK throws on `client.channel()` until `connectUser` has run (it crashed the staff
//      console, CHT-1984). `StreamSession.channel()` enforces the order itself, so a client cannot depend on the SDK for it;
//   2. the token provider: the first token comes from the session route, and a later token from the refresh call; a refusal of the refresh
//      is reported (`onTokenRefused`) and is the signal that the session has ended;
//   3. the stop guard: a sign-out during startup is a cancellation (`StreamSessionClosedError`), whether the SDK rejects the pending connect
//      (it does, 9.54.0) or lets it finish late (that connection is closed again and never used);
//   4. the listeners of the client: each is tracked, and `disconnect()` removes all of them;
//   5. disconnect: once, never rejects.
//
// What is NOT shared, because the two clients really differ: the offline store, the app state and network watching, and the timed reopen
// of the socket (the phone only); the join view, the membership and the state words of the workspace (the console only).
//
// This file imports no chat SDK. The client is a structural PORT (`StreamClientPort`): the real `StreamChat` satisfies it, and a test
// passes `FakeStreamClient` from `@lafayette/api-client/testing`. No native code can enter the web bundle through it.
/** `disconnect()` was called: the session is closed for good. */
export class StreamSessionClosedError extends Error {
    constructor() {
        super("the Stream session was closed");
        this.name = "StreamSessionClosedError";
    }
}
/** A channel was asked for before the user was connected. The rule of the SDK, made ours, so it holds for every client and every test. */
export class StreamNotConnectedError extends Error {
    constructor() {
        super("Connect the Stream user before making a channel");
        this.name = "StreamNotConnectedError";
    }
}
export class StreamSession {
    client;
    user;
    options;
    connecting = null;
    isConnected = false;
    isClosed = false;
    disconnecting = null;
    firstToken;
    unsubscribers = new Set();
    constructor(client, user, options) {
        this.client = client;
        this.user = user;
        this.options = options;
        this.firstToken = options.firstToken ?? null;
    }
    /** True once `connect()` has finished and until `disconnect()`. */
    get connected() {
        return this.isConnected && !this.isClosed;
    }
    get closed() {
        return this.isClosed;
    }
    /**
     * Connects the user. Calls share one attempt; a failed attempt is forgotten, so a later call tries again. It rejects with
     * `StreamSessionClosedError` when the session was closed before or while it connected (a connection that finished late is closed).
     * Any other rejection is the error of the SDK, for the adapter to classify.
     */
    connect() {
        if (this.isClosed)
            return Promise.reject(new StreamSessionClosedError());
        this.connecting ??= this.run().catch((error) => {
            this.connecting = null;
            throw error;
        });
        return this.connecting;
    }
    async run() {
        try {
            await this.client.connectUser(this.user, this.tokenProvider);
        }
        catch (error) {
            // The SDK (9.54.0) REJECTS a pending `connectUser` when `disconnectUser()` is called ("Connection was closed because disconnectUser()
            // was called"). That is a cancellation, not a failure of the connection: say so. A failure while the session is open stays a failure.
            if (this.isClosed)
                throw new StreamSessionClosedError();
            throw error;
        }
        if (this.isClosed) {
            // `disconnect()` arrived while the SDK was connecting. Its own disconnect may have come too early to close this connection.
            await this.closeClient();
            throw new StreamSessionClosedError();
        }
        this.isConnected = true;
    }
    tokenProvider = async () => {
        if (this.firstToken !== null) {
            const token = this.firstToken;
            this.firstToken = null;
            return token;
        }
        try {
            return await this.options.refreshToken();
        }
        catch (error) {
            this.options.onTokenRefused?.(error);
            throw error;
        }
    };
    /** A channel of the connected user. Throws `StreamNotConnectedError` before `connect()` has finished, and after `disconnect()`. */
    channel(type, id) {
        if (!this.connected)
            throw new StreamNotConnectedError();
        return this.client.channel(type, id);
    }
    /** Listens to an event of the client. The listener is removed by the returned function or by `disconnect()`. Nothing after a close. */
    listen(event, handler) {
        if (this.isClosed)
            return () => undefined;
        const subscription = this.client.on(event, handler);
        const remove = () => {
            this.unsubscribers.delete(remove);
            subscription.unsubscribe();
        };
        this.unsubscribers.add(remove);
        return remove;
    }
    /** Closes the session: removes every listener and disconnects the user. Safe to call twice and at any time. Never rejects. */
    disconnect() {
        this.isClosed = true;
        this.isConnected = false;
        this.disconnecting ??= (async () => {
            for (const remove of [...this.unsubscribers])
                remove();
            await this.closeClient();
        })();
        return this.disconnecting;
    }
    async closeClient() {
        try {
            await this.client.disconnectUser();
        }
        catch {
            // Already disconnected.
        }
    }
}
